import { z } from "zod";

/**
 * Suggested-sellers wire contract — single source of truth for web + mobile.
 *
 * Served by `GET /api/profile/suggested-users`, rendered by three surfaces that
 * each used to hand-declare the row shape: the feed's right `DiscoveryRail`
 * (`SuggestedSeller`), the profile page's `FollowSuggestionsCarousel`
 * (`SuggestedUser`) and mobile's `ProfileSuggestedUser`. The web pair had
 * already drifted — the rail's copy omitted `listingPreview` and
 * `listings_count` — and the rail additionally guessed the envelope
 * (`Array.isArray(data) ? data : data.items ?? data.users ?? []`) for a route
 * that has only ever answered `{ items }`. Same class of drift
 * `search-users.ts` was filed to close; same remedy.
 *
 * Schema-first: types derived via `z.infer` — see pin.ts header on why
 * bidirectional `pinned()` does not apply.
 */
// eslint-disable-next-line no-restricted-syntax -- schema-first declaration: ProfileSuggestedUser is z.infer<typeof ProfileSuggestedUserSchema>.
export const ProfileSuggestedUserSchema = z.object({
  id: z.string(),
  username: z.string(),
  avatar_url: z.string().nullable(),
  followers_count: z.number(),
  is_following: z.boolean(),
  /** First image of the seller's most recent live listing; null when they have none. */
  listingPreview: z.string().nullable(),
  // Additive seller stats (2026-07 cross-platform contract, lib/profile-stats.ts).
  // Always populated by the route today, but `.optional()` so older cached
  // payloads (mobile) still parse — same policy as `search-users.ts`. `rating`
  // is the seller-review average, 1 decimal, null with no published reviews.
  listings_count: z.number().optional(),
  rating: z.number().nullable().optional(),
  rating_count: z.number().optional(),
  sales_count: z.number().optional(),
});
export type ProfileSuggestedUser = z.infer<typeof ProfileSuggestedUserSchema>;

/**
 * The route answers `{ items }` on every path — guest fallback, the
 * `get_public_profiles_with_followers_by_ids` RPC, and its degraded
 * `get_public_profiles_with_followers` fallback all funnel through
 * `buildSuggestedItems`. `.catch` on the row array is deliberate: one
 * malformed row must not blank an entire discovery surface, so unparseable
 * rows are dropped rather than rejecting the response (`parseSuggestedSellers`
 * in `lib/profile/suggested-sellers.ts` owns that reduction).
 */
// eslint-disable-next-line no-restricted-syntax -- schema-first declaration: ProfileSuggestedUsersResponse is z.infer<typeof ProfileSuggestedUsersResponseSchema>.
export const ProfileSuggestedUsersResponseSchema = z.object({
  items: z.array(ProfileSuggestedUserSchema.nullable().catch(null)),
});
export type ProfileSuggestedUsersResponse = z.infer<
  typeof ProfileSuggestedUsersResponseSchema
>;

/** Request params for `GET /api/profile/suggested-users`. */
// eslint-disable-next-line no-restricted-syntax -- schema-first declaration: ProfileSuggestedUsersParams is z.infer<typeof ProfileSuggestedUsersParamsSchema>.
export const ProfileSuggestedUsersParamsSchema = z.object({
  /** Profile to leave out of the list — the page's own seller, or the viewer. */
  excludeProfileId: z.string().optional(),
  /** Route clamps to 1..20 and defaults to 12. */
  limit: z.number().int().positive().max(20).default(12),
});
export type ProfileSuggestedUsersParams = z.infer<
  typeof ProfileSuggestedUsersParamsSchema
>;
